import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Form,
  Input,
  Button,
  Upload,
  Select,
  Card,
  Typography,
  Row,
  Col,
  message,
  Checkbox,
} from "antd";
import {
  BankOutlined,
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  ShopOutlined,
  GlobalOutlined,
  LockOutlined,
  PlusOutlined,
  CheckCircleOutlined,
  InstagramOutlined,
  FacebookOutlined,
  LinkedinOutlined,
  TwitterOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import { fetchIndustries } from "../../api/industryApi";

const { Text } = Typography;
const { Option } = Select;

const CompanyRegister = ({ onSuccess, editingCompany, isAdminEdit = false }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [logoFileList, setLogoFileList] = useState([]);
  const [bannerFileList, setBannerFileList] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [locationLink, setLocationLink] = useState('');
  const [industriesLoading, setIndustriesLoading] = useState(false);

  const navigate = useNavigate();

  // Mocking fetchIndustries if import fails or for display purposes
  // Replace with actual API call
  useEffect(() => {
    const loadIndustries = async () => {
      try {
        setIndustriesLoading(true);
        const response = await fetchIndustries();
        // console.log('Industries API Response:', response);

        // Fix: Handle the correct response structure
        if (response && response.success && response.data) {
          setIndustries(response.data);
        } else if (response && response.industries) {
          setIndustries(response.industries);
        } else if (Array.isArray(response)) {
          setIndustries(response);
        } else {
          console.error('Unexpected industries response format:', response);
          setIndustries([]);
        }
      } catch (error) {
        console.error("Error fetching industries:", error);
        setIndustries([]);
      } finally {
        setIndustriesLoading(false);
      }
    };

    loadIndustries();
  }, []);


  // Populate form when editing
  useEffect(() => {
    if (editingCompany) {
      form.setFieldsValue({
        companyName: editingCompany.companyName,
        directorCeo: editingCompany.directorCeo,
        industry: editingCompany.industry,
        website: editingCompany.website,
        description: editingCompany.description || '',
        contactName: editingCompany.contactName,
        email: editingCompany.email,
        phone: editingCompany.phone,
        country: editingCompany.country || editingCompany.address?.country,
        state: editingCompany.state || editingCompany.address?.state,
        city: editingCompany.city || editingCompany.address?.city,
        pincode: editingCompany.pincode || editingCompany.address?.pincode,
        status: editingCompany.status,
        instagram: editingCompany.socialNetworks?.instagram || '',
        facebook: editingCompany.socialNetworks?.facebook || '',
        linkedin: editingCompany.socialNetworks?.linkedin || '',
        twitter: editingCompany.socialNetworks?.twitter || ''
      });

      // Set location link if exists
      if (editingCompany.location) {
        setLocationLink(editingCompany.location.address || '');
      }

      // Set logo if exists
      if (editingCompany.logo) {
        setLogoFileList([
          {
            uid: '-1',
            name: 'logo.png',
            status: 'done',
            url: editingCompany.logo,
          },
        ]);
      }

      // Set banner if exists
      if (editingCompany.banner) {
        setBannerFileList([
          {
            uid: '-1',
            name: 'banner.png',
            status: 'done',
            url: editingCompany.banner,
          },
        ]);
      }
    }
  }, [editingCompany, form]);

  const baseStyle = {
    fontFamily: "Rubik, sans-serif",
    fontSize: "14px",
    fontWeight: 400,
    color: "#333",
  };

  const inputStyle = {
    fontFamily: "Rubik, sans-serif",
    fontSize: "15px",
    borderRadius: "6px",
  };

  const labelStyle = {
    fontFamily: "Rubik, sans-serif",
    fontSize: "16px",
    fontWeight: 400,
    color: "#333",
    marginBottom: "6px",
  };

  const sectionStyle = {
    background: "#fafafa",
    border: "1px solid #f0f0f0",
    borderRadius: 8,
    padding: 20,
    marginBottom: 20,
  };

  const sectionHeaderStyle = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 16,
  };

  const onFinish = async (values) => {
    setLoading(true);

    try {
      // Location is now a plain link entered by the user (no map picker)
      const locationData = {
        coordinates: [0, 0],
        address: locationLink || ''
      };

      // Check if we have new files to upload
      const hasNewLogo = logoFileList.length > 0 && logoFileList[0].originFileObj;
      const hasNewBanner = bannerFileList.length > 0 && bannerFileList[0].originFileObj;
      const hasFiles = hasNewLogo || hasNewBanner;

      let requestData;
      let headers = {};

      // Use FormData only if we have files or it's a new registration
      if (!editingCompany || hasFiles) {
        const formData = new FormData();

        // Append all text fields
        formData.append("companyName", values.companyName);
        formData.append("directorCeo", values.directorCeo);
        formData.append("industry", values.industry);
        formData.append("website", values.website || "");
        formData.append("description", values.description || "");
        formData.append("contactName", values.contactName || values.directorCeo);
        formData.append("email", values.email);
        formData.append("phone", values.phone);
        formData.append("country", values.country);
        formData.append("state", values.state);
        formData.append("city", values.city);
        formData.append("pincode", values.pincode);

        // Send social networks as JSON string to reduce field count
        formData.append("socialNetworks", JSON.stringify({
          instagram: values.instagram || "",
          facebook: values.facebook || "",
          linkedin: values.linkedin || "",
          twitter: values.twitter || ""
        }));

        // Send location as JSON string to reduce field count
        formData.append("location", JSON.stringify(locationData));

        // Only append password for new companies
        if (!editingCompany && values.password) {
          formData.append("password", values.password);
        }

        // Append status if editing
        if (editingCompany && values.status) {
          formData.append("status", values.status);
        }

        // Append logo file if exists
        if (hasNewLogo) {
          formData.append("logo", logoFileList[0].originFileObj);
        }

        // Append banner file if exists
        if (hasNewBanner) {
          formData.append("banner", bannerFileList[0].originFileObj);
        }

        requestData = formData;
        headers['Content-Type'] = 'multipart/form-data';
      } else {
        // For editing without files, send as JSON
        requestData = {
          companyName: values.companyName,
          directorCeo: values.directorCeo,
          industry: values.industry,
          website: values.website || "",
          description: values.description || "",
          contactName: values.contactName || values.directorCeo,
          email: values.email,
          phone: values.phone,
          country: values.country,
          state: values.state,
          city: values.city,
          pincode: values.pincode,
          status: values.status,
          socialNetworks: {
            instagram: values.instagram || "",
            facebook: values.facebook || "",
            linkedin: values.linkedin || "",
            twitter: values.twitter || ""
          },
          location: locationData
        };
        headers['Content-Type'] = 'application/json';
      }

      // console.log('Request data:', requestData);

      let response;
      if (editingCompany) {
        // Update existing company
        response = await axios.put(
          `${process.env.REACT_APP_API_URL}/api/companies/${editingCompany._id}`,
          requestData,
          { headers }
        );
      } else {
        // Create new company
        response = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/auth/company-register`,
          requestData,
          { headers }
        );
      }

      // console.log("Registration response:", response.data);

      if (response.data.success) {
        message.success(editingCompany ? "Company updated successfully!" : "Registration submitted successfully! Awaiting admin approval.");
        form.resetFields();
        setLogoFileList([]);
        setBannerFileList([]);

        if (editingCompany) {
          // Call onSuccess for both admin and self-edit
          if (onSuccess) onSuccess();
        } else {
          // Store pending status in localStorage
          localStorage.setItem("pendingCompanyEmail", values.email);

          if (onSuccess) onSuccess();
        }
      }
    } catch (error) {
      console.error("Registration error:", error);
      message.error(
        error.response?.data?.message || (editingCompany ? "Update failed. Please try again." : "Registration failed. Please try again.")
      );
    } finally {
      setLoading(false);
    }
  };

  // ... (Upload props remain the same)
  const MAX_FILE_SIZE_MB = 5;
  const validateFileSizeBeforeUpload = (file) => {
    if (file.size / 1024 / 1024 > MAX_FILE_SIZE_MB) {
      message.error(`${file.name} is too large. Maximum allowed size is ${MAX_FILE_SIZE_MB}MB.`);
      return Upload.LIST_IGNORE;
    }
    return false;
  };

  const logoUploadProps = {
    beforeUpload: validateFileSizeBeforeUpload,
    onChange: ({ fileList }) => setLogoFileList(fileList.slice(-1)),
    fileList: logoFileList,
    listType: "picture-card",
    maxCount: 1,
  };

  const bannerUploadProps = {
    beforeUpload: validateFileSizeBeforeUpload,
    onChange: ({ fileList }) => setBannerFileList(fileList.slice(-1)),
    fileList: bannerFileList,
    listType: "picture-card",
    maxCount: 1,
  };


  return (
    <div
      style={{
        backgroundColor: "#fff",
        fontFamily: "Rubik, sans-serif",
      }}
    >
      <style>
        {`

          .ant-form-item {
            margin-bottom: 16px !important;
          }
          .ant-input,
          .ant-input-password input,
          .ant-select-selector {
            font-family: 'Rubik', sans-serif !important;
            font-size: 14px !important;
            color: #333 !important;
            height: 33px !important;
            line-height: 38px !important;
          }
          .ant-input-affix-wrapper {
            padding: 0 11px !important;
            height: 38px !important;
          }
          .ant-input-prefix {
            margin-right: 8px !important;
          }
          .ant-input::placeholder,
          .ant-input-password input::placeholder {
            font-family: 'Rubik', sans-serif !important;
            font-size: 14px !important;
            color: #bfbfbf !important;
            font-weight: 400 !important;
          }
          .ant-select-selection-placeholder {
            font-family: 'Rubik', sans-serif !important;
            font-size: 14px !important;
            color: #bfbfbf !important;
            font-weight: 400 !important;
            line-height: 36px !important;
          }
          .ant-select-single .ant-select-selector {
            height: 38px !important;
            padding: 0 11px !important;
          }
          .ant-select-selection-search-input {
            height: 36px !important;
          }
        `}
      </style>

      <Card
        style={{
          maxWidth: "1000px",
          margin: "0 auto",
          boxShadow: "none",
          padding: "0 10px",
        }}
        bodyStyle={{ padding: "10px" }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
        >
          {/* Company Details Section */}
          <div style={sectionStyle}>
            <div style={sectionHeaderStyle}>
              <BankOutlined style={{ color: "#470172", fontSize: "18px" }} />
              <Text strong style={{ ...baseStyle, fontSize: "17px", color: "#470172" }}>
                Company Details
              </Text>
            </div>

            <Row gutter={16}>
              <Col xs={24}>
                <Form.Item
                  label={<span style={labelStyle}>Company Name</span>}
                  name="companyName"
                  rules={[{ required: true, message: "Please enter company name" }]}
                >
                  <Input
                    prefix={<BankOutlined style={{ color: "#470172", fontSize: "14px" }} />}
                    placeholder="Enter your company name"
                    size="middle"
                    style={inputStyle}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label={<span style={labelStyle}>Director/CEO Name</span>}
                  name="directorCeo"
                  rules={[{ required: true, message: "Please enter director/CEO name" }]}
                >
                  <Input
                    prefix={<UserOutlined style={{ color: "#470172", fontSize: "14px" }} />}
                    placeholder="Enter full name"
                    size="middle"
                    style={inputStyle}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label={<span style={labelStyle}>Select Industry/Sector</span>}
                  name="industry"
                  rules={[{ required: true, message: "Please select an industry" }]}
                >
                  <Select
                    placeholder="Select your industry"
                    size="middle"
                    style={inputStyle}
                    suffixIcon={<ShopOutlined style={{ color: "#470172", fontSize: "14px" }} />}
                  >
                    {industries.map((ind) => (
                      <Option key={ind._id} value={ind.sectorName}>
                        {ind.sectorName}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label={<span style={labelStyle}>Website (Optional)</span>}
                  name="website"
                >
                  <Input
                    prefix={<GlobalOutlined style={{ color: "#470172", fontSize: "14px" }} />}
                    placeholder="https://www.yourcompany.com"
                    size="middle"
                    style={inputStyle}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label={<span style={labelStyle}>Company Logo</span>}
                  rules={[{ required: true, message: "Please upload logo" }]}
                >
                  <Upload {...logoUploadProps}>
                    {logoFileList.length < 1 && (
                      <div style={{ padding: "12px", textAlign: "center" }}>
                        <PlusOutlined style={{ fontSize: "20px", color: "#470172" }} />
                        <div style={{ ...baseStyle, fontSize: "12px", marginTop: "6px", color: "#666" }}>
                          Click to upload logo
                        </div>
                      </div>
                    )}
                  </Upload>
                  <Text style={{ ...baseStyle, fontSize: "12px", color: "#999", marginTop: "4px" }}>
                    Square format (512x512px), Max 5MB
                  </Text>
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item label={<span style={labelStyle}>Banner Image (Optional)</span>}>
                  <Upload {...bannerUploadProps}>
                    {bannerFileList.length < 1 && (
                      <div style={{ padding: "12px", textAlign: "center" }}>
                        <PlusOutlined style={{ fontSize: "20px", color: "#470172" }} />
                        <div style={{ ...baseStyle, fontSize: "12px", marginTop: "6px", color: "#666" }}>
                          Click to upload banner
                        </div>
                      </div>
                    )}
                  </Upload>
                  <Text style={{ ...baseStyle, fontSize: "12px", color: "#999", marginTop: "4px" }}>
                    1200x400px recommended, Max 5MB
                  </Text>
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item
                  label={<span style={labelStyle}>Company Description</span>}
                  name="description"
                  extra={<Text style={{ fontSize: "12px", color: "#999" }}>Provide details about your company to help others understand your business better</Text>}
                >
                  <Input.TextArea rows={4} placeholder="Brief description about your company, products, and services..." style={{ ...inputStyle, height: 'auto' }} />
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Social Networks Section */}
          <div style={sectionStyle}>
            <div style={sectionHeaderStyle}>
              <GlobalOutlined style={{ color: "#470172", fontSize: "18px" }} />
              <Text strong style={{ ...baseStyle, fontSize: "17px", color: "#470172" }}>
                Social Networks
              </Text>
            </div>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  label={
                    <span style={labelStyle}>
                      <InstagramOutlined style={{ color: "#E4405F", marginRight: "8px", fontSize: "16px" }} />
                      Instagram
                    </span>
                  }
                  name="instagram"
                >
                  <Input
                    placeholder="https://instagram.com/yourcompany"
                    size="middle"
                    style={inputStyle}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label={
                    <span style={labelStyle}>
                      <FacebookOutlined style={{ color: "#1877F2", marginRight: "8px", fontSize: "16px" }} />
                      Facebook
                    </span>
                  }
                  name="facebook"
                >
                  <Input
                    placeholder="https://facebook.com/yourcompany"
                    size="middle"
                    style={inputStyle}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label={
                    <span style={labelStyle}>
                      <LinkedinOutlined style={{ color: "#0A66C2", marginRight: "8px", fontSize: "16px" }} />
                      LinkedIn
                    </span>
                  }
                  name="linkedin"
                >
                  <Input
                    placeholder="https://linkedin.com/company/yourcompany"
                    size="middle"
                    style={inputStyle}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label={
                    <span style={labelStyle}>
                      <TwitterOutlined style={{ color: "#1DA1F2", marginRight: "8px", fontSize: "16px" }} />
                      Twitter / X
                    </span>
                  }
                  name="twitter"
                >
                  <Input
                    placeholder="https://twitter.com/yourcompany"
                    size="middle"
                    style={inputStyle}
                  />
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Contact Information Section */}
          <div style={sectionStyle}>
            <div style={sectionHeaderStyle}>
              <MailOutlined style={{ color: "#470172", fontSize: "18px" }} />
              <Text strong style={{ ...baseStyle, fontSize: "17px", color: "#470172" }}>
                Contact Information
              </Text>
            </div>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  label={<span style={labelStyle}>Contact Person Name</span>}
                  name="contactName"
                >
                  <Input
                    prefix={<UserOutlined style={{ color: "#470172", fontSize: "14px" }} />}
                    placeholder="Leave blank if same as CEO"
                    size="middle"
                    style={inputStyle}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label={<span style={labelStyle}>Email Address</span>}
                  name="email"
                  rules={[
                    { required: true, message: "Please enter email" },
                    { type: "email", message: "Please enter valid email" },
                  ]}
                >
                  <Input
                    prefix={<MailOutlined style={{ color: "#470172", fontSize: "14px" }} />}
                    placeholder="company@example.com"
                    size="middle"
                    style={inputStyle}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label={<span style={labelStyle}>Phone/Mobile</span>}
                  name="phone"
                  rules={[{ required: true, message: "Please enter phone number" }]}
                >
                  <Input
                    prefix={<PhoneOutlined style={{ color: "#470172", fontSize: "14px" }} />}
                    placeholder="+91 98765 43210"
                    size="middle"
                    style={inputStyle}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label={<span style={labelStyle}>Country</span>}
                  name="country"
                  rules={[{ required: true, message: "Please enter country" }]}
                >
                  <Input
                    placeholder="Enter country"
                    size="middle"
                    style={inputStyle}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label={<span style={labelStyle}>State</span>}
                  name="state"
                  rules={[{ required: true, message: "Please enter state" }]}
                >
                  <Input
                    placeholder="Enter state"
                    size="middle"
                    style={inputStyle}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label={<span style={labelStyle}>City</span>}
                  name="city"
                  rules={[{ required: true, message: "Please enter city" }]}
                >
                  <Input
                    placeholder="Enter city"
                    size="middle"
                    style={inputStyle}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label={<span style={labelStyle}>Pincode</span>}
                  name="pincode"
                  rules={[{ required: true, message: "Please enter pincode" }]}
                >
                  <Input
                    placeholder="Enter pincode"
                    size="middle"
                    style={inputStyle}
                  />
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Company Location Section */}
          <div style={sectionStyle}>
            <div style={sectionHeaderStyle}>
              <EnvironmentOutlined style={{ color: "#470172", fontSize: "18px" }} />
              <Text strong style={{ ...baseStyle, fontSize: "17px", color: "#470172" }}>
                Company Location
              </Text>
            </div>

            <Form.Item
              label={<span style={labelStyle}>Location Link</span>}
              extra={<Text style={{ fontSize: "12px", color: "#999" }}>Paste a Google Maps (or other map) link to your company's location</Text>}
            >
              <Input
                prefix={<EnvironmentOutlined style={{ color: "#470172", fontSize: "14px" }} />}
                placeholder="https://maps.google.com/?q=..."
                size="middle"
                value={locationLink}
                onChange={(e) => setLocationLink(e.target.value)}
                style={inputStyle}
              />
            </Form.Item>
          </div>

          {/* Account Security Section */}
          <div style={sectionStyle}>
            <div style={sectionHeaderStyle}>
              <LockOutlined style={{ color: "#470172", fontSize: "18px" }} />
              <Text strong style={{ ...baseStyle, fontSize: "17px", color: "#470172" }}>
                Account Security
              </Text>
            </div>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  label={<span style={labelStyle}>Password</span>}
                  name="password"
                  rules={[
                    { required: !editingCompany, message: "Please enter password" },
                    { min: 6, message: "Password must be at least 6 characters" },
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined style={{ color: "#470172", fontSize: "14px" }} />}
                    placeholder={editingCompany ? "Leave blank to keep current password" : "Create a strong password"}
                    size="middle"
                    style={inputStyle}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label={<span style={labelStyle}>Confirm Password</span>}
                  name="confirmPassword"
                  dependencies={["password"]}
                  rules={[
                    { required: !editingCompany, message: "Please confirm password" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("password") === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error("Passwords do not match"));
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined style={{ color: "#470172", fontSize: "14px" }} />}
                    placeholder="Re-enter your password"
                    size="middle"
                    style={inputStyle}
                  />
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Terms and Submit */}
          {!editingCompany && (
            <Card
              style={{
                background: "#f0e5f5",
                marginBottom: "20px",
                borderRadius: "8px",
                border: "1px solid #d4b5e8",
              }}
              bodyStyle={{ padding: "12px 16px" }}
            >
              <Form.Item
                name="terms"
                valuePropName="checked"
                style={{ marginBottom: 0 }}
                rules={[
                  {
                    validator: (_, value) =>
                      value
                        ? Promise.resolve()
                        : Promise.reject(new Error("You must agree to the terms and conditions")),
                  },
                ]}
              >
                <Checkbox style={{ ...baseStyle, fontSize: "15px" }}>
                  I agree to the{" "}
                  <a href="/terms" target="_blank" rel="noopener noreferrer">
                    Terms of Service
                  </a>{" "}
                  and{" "}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer">
                    Privacy Policy
                  </a>.
                </Checkbox>
              </Form.Item>
            </Card>
          )}

          <Form.Item style={{ marginTop: "8px", marginBottom: 0, textAlign: "center" }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              size="small"
              style={{
                ...baseStyle,
                height: "42px",
                fontSize: "17px",
                fontWeight: 500,
                width: "40%",
                backgroundColor: "#470172",
                borderColor: "#470172",
                color: "#fff",
              }}
              icon={<CheckCircleOutlined />}
            >
              {loading ? (editingCompany ? "Updating..." : "Registering...") : (editingCompany ? "Update Profile" : "Complete Registration")}
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default CompanyRegister;